import { XIcon } from 'lucide-react'
import { BodyText, Button, HeadingText, Modal, ModalHeader } from '../../../shared/ui'
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../../../shared/ui/input-otp'
import { cn } from '../../../shared/lib'
import { useEffect, useState } from 'react'
import { Danger } from 'iconsax-reactjs'
import { toast } from 'sonner'
import useProfile from '../hooks/useProfile'
import { useNavigate } from 'react-router'
import { PAGES } from '../../../shared/lib/pageUrlConstants'

interface OtpModalProps {
  isOpen: 'change-password' | 'setup-2fa' | 'regenerate-backup-codes' | 'change-trx-status' | 'confirm-transfer' | ''
  onOpenChange: (open: string) => void
  handleAdminChangePassword: (mfaCode: string) => Promise<any>
  isLoading: boolean
  onSuccess: (data: any) => void
}

const OtpModal = ({ isOpen, onOpenChange, handleAdminChangePassword, isLoading, onSuccess }: OtpModalProps) => {
  const { handleRegenrateBackupCode, regenrateBackupCodePending, handleResetMfa, resetMfaPending } = useProfile()
  const [value, setValue] = useState('')
  const [isError, setIsError] = useState(false)
  const [errorMsg, setErrorMsg] = useState('')
  const navigate = useNavigate()

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    try {
      if (isOpen === 'change-password') {
        const res = await handleAdminChangePassword(value)
        toast.success(res?.message || 'Password changed successfully')
        onOpenChange('')
        onSuccess('')
        setValue('')
      } else if (isOpen === 'setup-2fa') {
        const res = await handleResetMfa(value)
        toast.success(res?.message || '2FA setup successfully')
        navigate(PAGES.setup2fa, { state: res.data })
        onOpenChange('')
        setValue('')
      } else if (isOpen === 'regenerate-backup-codes') {
        const res = await handleRegenrateBackupCode(value)
        onSuccess(res?.data?.backupCodes)
        toast.success(res?.message || 'Backup codes regenerated successfully')
        onOpenChange('')
        setValue('')
      } else if (isOpen === 'change-trx-status' || isOpen === 'confirm-transfer') {
        await handleAdminChangePassword(value)
      }
    } catch (error: any) {
      console.log({ error })
      setIsError(true)
      setErrorMsg(error?.response?.data?.message || 'Invalid OTP')
    }
  }

  useEffect(() => {
    // clear error after 4 seconds
    if (isError) {
      setTimeout(() => {
        setIsError(false)
        setErrorMsg('')
        setValue('')
      }, 4000)
    }
  }, [isError])

  const isPending = isLoading || regenrateBackupCodePending || resetMfaPending

  return (
    <Modal open={!!isOpen} onOpenChange={() => {
      onOpenChange('')
      setValue('')
      setIsError(false)
      setErrorMsg('')
    }} contentClassName='max-w-[560px] rounded-[12px] w-full'>
      <ModalHeader className="flex  p-5 flex-col gap-2">
        <div className="flex items-center gap-2 justify-between w-full">
          <HeadingText className="text-2xl w-full text-center font-semibold tracking-[-2.5%] text-[#161827]">
            Authentication required
          </HeadingText>
          <XIcon onClick={() => onOpenChange('')} className="cursor-pointer" />
        </div>
        <BodyText className="text-[#6F739B] text-center leading-[24px] tracking-[-1.5%]  text-base font-[400]">
          Please enter the 6-digit code from your authenticator app to continue.
        </BodyText>
      </ModalHeader>
      <form onSubmit={onSubmit}>
        <div className="flex flex-col gap-2 p-6 justify-center items-center">
          <InputOTP maxLength={6} value={value} onChange={(value) => setValue(value)} autoFocus>
            <InputOTPGroup className="flex gap-4">
              <InputOTPSlot index={0} className={cn((value.length === 6 && isError) && "bg-error-20")} />
              <InputOTPSlot index={1} className={cn((value.length === 6 && isError) && "bg-error-20")} />
              <InputOTPSlot index={2} className={cn((value.length === 6 && isError) && "bg-error-20")} />
              <InputOTPSlot index={3} className={cn((value.length === 6 && isError) && "bg-error-20")} />
              <InputOTPSlot index={4} className={cn((value.length === 6 && isError) && "bg-error-20")} />
              <InputOTPSlot index={5} className={cn((value.length === 6 && isError) && "bg-error-20")} />
            </InputOTPGroup>
          </InputOTP>
          {(isError && errorMsg) && <p className="text-error-30 flex items-center gap-1 font-[350] text-xs"><Danger size={20} /> <span>{errorMsg}</span></p>}
        </div>
        <div className='flex p-5 gap-3 items-center border-t border-primary-80 justify-end'>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => onOpenChange('')}
            disabled={isPending}
            className=" text-base font-[450] hover:bg-transparent hover:text-gray-30"
          >
            Back
          </Button>
          <Button
            type="submit"
            size="lg"
            disabled={!value || value.length !== 6 || isPending}
            className="!h-[40px] rounded-[8px] text-base font-[450] bg-[#161827] hover:bg-[#20243a]"
          >
            {isPending ? "Loading..." : isOpen === 'change-password' ? "Change Password" : isOpen === 'setup-2fa' ? "Setup 2FA" : isOpen === 'regenerate-backup-codes' ? "Regenerate Backup Codes" : isOpen === 'change-trx-status' ? "Change Transaction Status" : isOpen === 'confirm-transfer' ? "Confirm Transfer" : ""}
          </Button>
        </div>
      </form>
    </Modal>
  )
}

export default OtpModal